import type Partner       from "@/domains/Partners/Partner";
import type TransportZone from "@/domains/TransportZones/TransportZone";

import Core               from "@/core/Core";
import TimeStamp          from "@/core/types/TimeStamp";
import Dictionary         from "@/core/types/Dictionary";
import Serializable       from "@/core/misc/mixins/Serializable";
import Assembly           from "@/domains/Assemblies/Assembly";
import Shipment           from "@/domains/Shipments/Shipment";
import TransportOrder     from "@/domains/TransportOrders/TransportOrder";
import OrderPosition      from "@/domains/Orders/OrderPosition";
import Product            from "@/domains/Products/Product";
import CargoUnit          from "@/domains/CargoUnits/CargoUnit";
import IntRange           from "@/core/types/IntRange";
import DateSimple         from "@/core/types/DateSimple";
import Weight             from "@/core/types/Weight";
import Qty                from "@/core/types/Qty";
import Volume             from "@/core/types/Volume";

class TransportableDocument extends Serializable {
    id: number;

    createdAt: TimeStamp;
    documentClass: string;

    receiver: Partner;
    receiverTransportZone: TransportZone;

		sender: Partner;
    senderTransportZone: TransportZone;

		storeTemperatureRange: IntRange;

		// Дата готовности
		arrivalDatePlanned: DateSimple;

    /** Паллетоместа */
    cargoUnits: Dictionary<number | string, CargoUnit> = new Dictionary();

		get isAssembly()       { return this.documentClass === 'Assembly' }
		get isShipment()       { return this.documentClass === 'Shipment' }
		get isTransportOrder() { return this.documentClass === 'TransportOrder' }
		get isOrderPosition()  { return this.documentClass === 'OrderPosition' }
		get isProduct()        { return this.documentClass === 'Product' }

		get canConsolidate()   { return this.isAssembly || this.isShipment || this.isTransportOrder }
		get canPickupOrderPosition() { return this.isOrderPosition }
		get canReturnOrderPosition() { return this.isOrderPosition }
		get canMoveInternalProduct() { return this.isProduct }

    get cargoUnitsCount(): Qty {
      return this.cargoUnits.reduce((total: Qty, item: CargoUnit) => {
        if (item.qty instanceof Qty) {
          return total.plus(item.qty);
        } else {
          return total;
        }
      }, Qty.zero());
    }

    get cargoWeightGross(): Weight {
      return this.cargoUnits.reduce((total: Weight, item: CargoUnit) => {
        const weight = item.totalWeightGross;

        if (weight instanceof Weight) {
          return total.plus(weight);
        } else {
          return total;
        }
      }, Weight.zero());
    }

    get cargoWeightNet(): Weight {
      return this.cargoUnits.reduce((total: Weight, item: CargoUnit) => {
        const weight = item.totalWeightNet;

        if (weight instanceof Weight) {
          return total.plus(weight);
        } else {
          return total;
        }
      }, Weight.zero());
    }

    get cargoVolume(): Volume {
      return this.cargoUnits.reduce((total: Volume, item: CargoUnit) => {
        const volume = item.totalVolume;

        if (volume instanceof Volume) {
          return total.plus(volume);
        } else {
          return total;
        }
      }, Volume.zero());
    }

    static fromServer(data, result = new TransportableDocument()) {
        const transportableDocument = super.fromServer(data, result);

        transportableDocument.createdAt = TimeStamp.fromServer(data.createdAt);

        const cargoUnitsMap = new Dictionary<number | string, CargoUnit>();
        cargoUnitsMap.add(Core.utils.hydrateArray(data.cargoUnits, CargoUnit.fromServer));
        transportableDocument.cargoUnits = cargoUnitsMap;

				transportableDocument.storeTemperatureRange = IntRange.fromServer(data.storeTemperatureRange);
				transportableDocument.arrivalDatePlanned = DateSimple.fromServer(data.arrivalDatePlanned);

        return transportableDocument;
    }

    // ide врёт, этот метод используется
    get documentClassStringRepresentation(): string {
        switch (this.documentClass) {
            case 'Assembly':       return Assembly.entityTypeName;
            case 'Shipment':       return Shipment.entityTypeName;
            case 'TransportOrder': return TransportOrder.entityTypeName;
            case 'OrderPosition':  return OrderPosition.entityTypeName;
            case 'Product':        return Product.entityTypeName;
            default:               return '--';
        }
    }
}

export default TransportableDocument;
